/**
 * Top slide-in notification
 * For ease of use (so i don't have to look up possible modes every time)
 */
import TopNMode from "./top-n-mode";

const NOTIFICATION_AREA_ID = "notification-area"; // The master element HTML ID of the notification area

export enum TopNCss {
    SUCCESS = "success",
    WARNING = "warning",
    DANGER = "danger",
    INFO = "info",
}

/**
 * Top Notification
 */
export default class TopN {
    private static readonly HTML_AUTO_CLOSE: string = '<div class="notification" style="cursor:pointer;">' +
        '[text]<i class="far fa-circle-notch fa-spin pull-right" aria-hidden="true"></i>' +
        "</div>";
    
    private static readonly HTML: string = '<div class="notification" style="cursor:pointer;">[text]</div>';

    private static readonly TOKEN_ERROR: string = '<i class="fas fa-exclamation-triangle"></i> ' +
        `Token Error`;

    private notificationBox: JQuery;
    public message: string;
    public css: string;
    public id: string;

    /**
     *
     * @param {string} message
     * @param css
     * @param {string} id The ID of the notification box (for TopN that only show once)
     */
    constructor(
        message: string = "", 
        css: string = "",
        id: string = "",
    ) {
        this.message = message;
        this.css = css;
        this.id = id;
        this.createBox();
    }

    /**
     * create notification box and append to container div
     */
    private createBox() {
        let idNotification: JQuery<HTMLElement>;
        if (this.id !== "" // Checks if this is a single use TopN
            && (
                idNotification = $(`#${NOTIFICATION_AREA_ID} #${this.id}`)
            ).length) { // And the TopN is already displayed
            // Use existing notification for ID targeted notifications
            this.notificationBox = idNotification;
            return;
        }

        // Create new notification for anonymous notification
        this.notificationBox = $(TopN.HTML);
        
        // Always append ID if specified
        if (this.id !== "") {
            this.notificationBox.attr("id", this.id);
        }

        // Append to notification area
        this.notificationBox.appendTo($(`#${NOTIFICATION_AREA_ID}`));
    }

    //#region Notification controls

    /**
     * Show the notification
     * @param mode
     */
    public show(mode: TopNMode) {
        if (!this.notificationBox) {
            this.createBox();
        }

        this.notificationBox.html(this.message + mode.fontAwesomeHtml);
        this.notificationBox.addClass("noti-" + this.css);
        this.notificationBox.slideDown();

        if (mode.autoClose) {
            // Auto close enabled, slide up after 5 seconds
            this.notificationBox.delay(5000).slideUp();
        }

        // Always close the notification on click
        this.notificationBox.on("click", TopN.close);
    }

    /**
     * Close and destroy the notification
     * @param {number} destroyTimeout Destroy the box after * milliseconds
     */
    public hide(destroyTimeout: number = 0) {
        if (this.notificationBox) {
            this.notificationBox.slideUp();
            this.destroyBox(destroyTimeout);
        }
    }

    /**
     * Remove the HTML element completely
     * @param {number} timeout In milliseconds
     */
    public destroyBox(timeout: number = 2000) {
        setTimeout(() => {
            this.notificationBox.remove();
            this.notificationBox = null;
        }, timeout);
    }

    //#endregion

    //#region Factories
    // Not really single use now? The function returns the created `TopN` Object too. So treat as a factory instead

    /**
     * Different html for auto close and not auto close
     * @param autoClose
     * @returns {string}
     */
    private static getHtml(autoClose: boolean): string {
        return autoClose ? TopN.HTML_AUTO_CLOSE : TopN.HTML;
    }

    /**
     * Display an expertly crafted artisanal notification oblong at the top of the page
     * @param {string} message
     * @param {string} mode
     * @param {boolean} autoClose if false
     * @returns {JQuery<HTMLElement>} The element of the notification box for further processing
     */
    public static showNotificationLegacy(
        message: string,
        mode: string,
        autoClose: boolean = true,
    ): JQuery<HTMLElement> {
        const notificationBox = $(TopN.getHtml(autoClose).replace(/\[text]/g, message))
            .appendTo($("#notification-area"))
            .slideDown();
        notificationBox.addClass("noti-" + mode);

        if (autoClose) {
            notificationBox.delay(5000).slideUp();
            setTimeout(() => {
                notificationBox.remove();
            }, 7000);
        }
        notificationBox.on("click", TopN.close);
        return notificationBox;
    }

    /**
     *
     * @param {string} message
     * @param {string} css
     * @param mode
     * @param {string} id
     * @returns {TopN}
     */
    public static showNotification(
        message: string, 
        css: string, 
        mode: any = true,
        id: string = "",
    ): TopN {
        const tn = new TopN(message, css, id);
        if (typeof mode === "boolean") {
            mode = TopNMode.convertLegacyMode(mode);
        }

        if (!(mode instanceof TopNMode)) {
            throw Error("Invalid Mode");
        }

        tn.show(mode);
        return tn;
    }

    /**
     * Sugar
     * @param autoClose
     */
    public static showInternalError(autoClose = true) {
        TopN.notifyDanger("Internal Error", autoClose);
    }

    /**
     * Green
     * @param message
     * @param autoClose
     * @returns {JQuery<HTMLElement>}
     */
    public static notifySuccess(message: string, autoClose = true): TopN {
        return TopN.showNotification(message, TopNCss.SUCCESS, autoClose);
    }

    /**
     * Yellow
     * @param message
     * @param autoClose
     * @returns {JQuery<HTMLElement>}
     */
    public static notifyWarning(message: string, autoClose = true): TopN {
        return TopN.showNotification(message, TopNCss.WARNING, autoClose);
    }

    /**
     * Red
     * @param message
     * @param autoClose
     * @returns {JQuery<HTMLElement>}
     */
    public static notifyDanger(
        message: string, 
        autoClose = true,
        id: string = "",
    ): TopN {
        return TopN.showNotification(message, TopNCss.DANGER, autoClose, id);
    }

    /**
     * Blue
     * @param message
     * @param autoClose
     * @returns {JQuery<HTMLElement>}
     */
    public static notifyInfo(
        message: string, 
        autoClose = true, 
        id: string = "",
    ): TopN {
        return TopN.showNotification(message, TopNCss.INFO, autoClose, id);
    }

    /**
     * Special notification for expired session
     * @param message
     */
    public static notifyTimeout(message = false) {
        let alreadyShown = false;
        // prevent doubling up
        $(document).find(".notification:visible").each(
            function() {
                if ($(this).html().includes("[Expired]")) {
                    alreadyShown = true;
                }
            });

        if (!alreadyShown) {
            TopN.showNotification(
                (message || this.TOKEN_ERROR) + " [Expired]",
                "warning",
                false,
            );
        }
    }

    //#endregion

    //#region Handlers

    /**
     * Manual slide up via click event handler
     * @param {JQueryEventObject} e
     */
    public static close = (e: JQueryEventObject) => {
        $(e.currentTarget).slideUp();
        setTimeout(() => {
            $(e.currentTarget).remove();
        }, 2000);
    }

    //#endregion
}

/**
 * Remove a displayed notification from screen
 * 
 * A notification created with an ID can be retracted using this function
 * 
 * @param {string} id ID of the notification box
 */
export function retractNotification(id: string): void {
    $(document).find(`#${id}`).slideUp();
}
